// ============================================================================
// Book a Delhivery — standalone tab. Enter any PO number to run the full Delhivery flow
// (quote → vehicle pick → book, or view live status if already booked). It reuses the same
// DelhiveryBookingModal the OMS logistics board launches per row, so there's one source of truth
// for the booking UI. Also hosts "Book dispatch due": load today's due-to-dispatch POs (filtered by
// committed delivery date) and bulk-book them via the shared bulk modal. Admin-only (route + nav).
// ============================================================================
import { Button, Card, DatePicker, Input, Space, Tooltip, Typography, message } from "antd";
import { CarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import React from "react";
import DelhiveryBookingModal from "../oms/logistics/delhivery_booking_modal";
import DelhiveryBulkModal from "../oms/logistics/delhivery_bulk_modal";
import { DelhiveryBulkQuoteRow, getDelhiveryDispatchDue } from "../../apis/po.api";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DelhiveryBooking: React.FC = () => {
  const [poInput, setPoInput] = React.useState("");
  // The PO whose modal is open (null = closed). Set on submit, cleared on modal close.
  const [activePo, setActivePo] = React.useState<string | null>(null);

  const openModal = () => {
    const po = poInput.trim();
    if (po) setActivePo(po);
  };

  // --- Book dispatch due ---------------------------------------------------------------------
  // Loads the dispatch-due worklist (live quotes, slow) and seeds the bulk-book modal with every
  // eligible PO — all pre-selected, with apply-to-all contacts. Delivery-date range defaults to
  // today–today; cleared → backend applies its trailing-week default.
  const [dueLoading, setDueLoading] = React.useState(false);
  const [dueDeliveryRange, setDueDeliveryRange] = React.useState<
    [dayjs.Dayjs, dayjs.Dayjs] | null
  >([dayjs(), dayjs()]);
  // Express / regular mode filter — "all" sends no mode param (both). Synced to the applied filter
  // the response echoes back.
  const [dueMode, setDueMode] = React.useState<"all" | "express" | "regular">("all");
  const [bulkPos, setBulkPos] = React.useState<string[]>([]);
  const [bulkPrequoted, setBulkPrequoted] = React.useState<DelhiveryBulkQuoteRow[]>([]);
  const [bulkNote, setBulkNote] = React.useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = React.useState(false);

  // Fetch the dispatch-due list for a given mode, map it into the bulk modal's row shape, and set the
  // modal state. Shared by the initial "Book dispatch due" click and the in-modal mode toggle.
  // Returns whether there were any eligible rows. Does NOT open the modal (caller decides).
  const runDispatchDue = async (mode: "all" | "express" | "regular"): Promise<boolean> => {
    setDueLoading(true);
    const res = await getDelhiveryDispatchDue({
      max_quotes: 200,
      delivery_date_from: dueDeliveryRange?.[0]?.format("YYYY-MM-DD"),
      delivery_date_to: dueDeliveryRange?.[1]?.format("YYYY-MM-DD"),
      mode: mode === "all" ? undefined : mode,
    });
    setDueLoading(false);
    if (!res) return false; // getDelhiveryDispatchDue already surfaced the error (incl. 400 bad-date detail)
    setDueMode(res.mode ?? "all"); // reflect the applied mode filter the response echoes
    const hidden = res.excluded_already_booked;
    if (res.truncated) {
      message.warning(
        `Showing the first ${res.eligible_count} eligible of ${res.candidate_count} due POs — book these, then run again for the rest.`
      );
    }
    if (hidden > 0) {
      message.info(`${hidden} PO${hidden === 1 ? "" : "s"} already booked and hidden.`);
    }
    if (res.eligible.length === 0) {
      message.info(
        hidden > 0
          ? `Nothing new to dispatch — ${hidden} already booked (hidden).`
          : "Nothing eligible to dispatch right now."
      );
    }
    // Active delivery window from the response (what the backend actually applied) → chip in the modal.
    const dFrom = dayjs(res.delivery_date_from);
    const dTo = dayjs(res.delivery_date_to);
    setBulkNote(
      res.delivery_date_from === res.delivery_date_to
        ? `Delivery: ${dFrom.format("DD MMM")}`
        : `Delivery: ${dFrom.format("DD MMM")}–${dTo.format("DD MMM")}`
    );
    // The dispatch-due rows are already quoted — hand them to the bulk modal directly (as its
    // DelhiveryBulkQuoteRow shape) so it skips a second, slow quote and shows them for review.
    const prequoted: DelhiveryBulkQuoteRow[] = res.eligible.map((r) => ({
      po_number: r.po_number,
      bookable: true,
      vehicle_mode: r.vehicle_mode,
      fare: r.fare,
      vehicles: r.vehicles,
      computed_weight_kg: r.computed_weight_kg,
      max_item_length_ft: r.max_item_length_ft,
      items: r.items,
      consolidated: r.booking_id != null && !!r.consolidated_pos?.length,
      consolidated_pos: r.consolidated_pos ?? null,
      booking_id: r.booking_id ?? undefined,
      pickup_contact_number: r.pickup_contact_number ?? null,
      drop_contact_number: r.drop_contact_number ?? null,
      pickup_contact_options: r.pickup_contact_options,
      drop_contact_options: r.drop_contact_options,
      assigned_to_name: r.assigned_to_name ?? null,
      assigned_to_contact: r.assigned_to_contact ?? null,
      leg_commited_dispatch_time: r.leg_commited_dispatch_time ?? null,
      leg_commited_delivery_time: r.leg_commited_delivery_time ?? null,
      delivery_planned_eta: r.delivery_planned_eta ?? null,
      has_escalation: r.has_escalation ?? false,
      escalation: r.escalation ?? null,
      mode: r.mode ?? null,
    }));
    setBulkPrequoted(prequoted);
    setBulkPos(res.eligible.map((r) => r.po_number));
    return res.eligible.length > 0;
  };

  const onLoadDispatchDue = async () => {
    if (dueLoading) return;
    const hasAny = await runDispatchDue(dueMode);
    if (hasAny) setBulkOpen(true); // only open when there's something to show
  };

  // The modal's mode toggle (dispatch-due only) — re-fetch in place; the modal stays open.
  const onDueModeChange = (mode: "all" | "express" | "regular") => {
    if (dueLoading) return;
    runDispatchDue(mode);
  };

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <Title level={3}>Book a Delhivery</Title>
      <Text type="secondary">
        Enter a PO number to check eligibility and book — or view the live status of an existing
        Delhivery booking.
      </Text>

      <Card style={{ marginTop: 16 }}>
        <Space.Compact style={{ width: "100%" }}>
          <Input
            placeholder="PO number"
            value={poInput}
            onChange={(e) => setPoInput(e.target.value)}
            onPressEnter={openModal}
            allowClear
          />
          <Button type="primary" disabled={!poInput.trim()} onClick={openModal}>
            Open
          </Button>
        </Space.Compact>
      </Card>

      <Card style={{ marginTop: 16 }} title="Book dispatch due">
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Load date wise filtered due-to-dispatch POs from <strong>horamavu/rebound</strong> and bulk-book them on Delhivery.
        </Text>
        <Space wrap size={12} align="end">
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
              Delivery date range
            </Text>
            <Tooltip title="Filter dispatch-due POs by committed delivery date (applies within the dispatch window)">
              <RangePicker
                format="DD MMM YYYY"
                value={dueDeliveryRange}
                onChange={(v) =>
                  setDueDeliveryRange(v && v[0] && v[1] ? [v[0], v[1]] : null)
                }
              />
            </Tooltip>
          </div>
          <Button
            type="primary"
            icon={<CarOutlined />}
            loading={dueLoading}
            disabled={dueLoading}
            onClick={onLoadDispatchDue}
          >
            Book dispatch due
          </Button>
        </Space>
      </Card>

      {activePo && (
        <DelhiveryBookingModal
          poNumber={activePo}
          open={!!activePo}
          onClose={() => setActivePo(null)}
        />
      )}

      <DelhiveryBulkModal
        poNumbers={bulkPos}
        prequoted={bulkPrequoted}
        note={bulkNote}
        mode={dueMode}
        onModeChange={onDueModeChange}
        busy={dueLoading}
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onBooked={() => setBulkOpen(false)}
      />
    </div>
  );
};

export default DelhiveryBooking;
